import type {
  CarKeepersClubContent,
  CtaContent,
  DecisionContent,
  FaqChipContent,
  FaqContent,
  FaqItemContent,
  FooterContent,
  HeroContent,
  LocationContent,
  NavContent,
  PricingContent,
  SeoContent,
  ServiceCardContent,
  ServicesContent,
} from './content.schema';

function service(
  number: string,
  title: string,
  body: string,
  bullet1: string,
  bullet2: string,
  bullet3: string,
): ServiceCardContent {
  return { number, title, body, bullet1, bullet2, bullet3 };
}

function chip(label: string, topic: string): FaqChipContent {
  return { label, topic };
}

function faq(
  question: string,
  answer: string,
  topic: string,
  keywords?: string,
): FaqItemContent {
  return { question, answer, topic, ...(keywords ? { keywords } : {}) };
}

const enSeo: SeoContent = {
  metaTitle: 'Madeira - Car Keepers Club',
  metaDescription:
    "Car Keepers Club & Repair Shop on Madeira. A place where we continuously look after your car, preventing costly repairs, and preserving your car's value.",
};

const enNav: NavContent = {
  servicesLabel: 'Services',
  aboutLabel: 'About us',
  pricingLabel: 'Pricing',
  locationLabel: 'Location',
  faqLabel: 'FAQ',
  whatsappLabel: 'WhatsApp us',
  langSwitchLabel: 'PT',
};

const enHero: HeroContent = {
  title: 'Car Care made simple',
  lead:
    "A place where we continuously look after your car, preventing costly repairs, and preserving your car's value.",
  primaryCta: 'Schedule a visit',
  secondaryCta: 'See pricing',
  cardTitle: 'Continuous car care.',
  cardBody:
    "We keep you updated on your car's safety and condition, manage records and paperwork, and let you know if anything needs to be fixed or replaced.",
  tag1: 'Safety updates',
  tag2: 'Records & paperwork',
  tag3: 'Service history',
  tag4: 'Preserve resale value',
  quickLinksLabel: 'Quick links',
  quickLink1: 'Club & repair shop services',
  quickLink2: 'Membership & repair shop prices',
  quickLink3: 'Location & contacts',
  quickLink4: 'Questions & answers',
};

const enServices: ServicesContent = {
  kicker: 'Club & repair shop services',
  title: 'How we look after your car',
  body:
    'In the club, we keep you updated on your car’s safety and condition. In the repair shop, we handle most maintenance tasks and confirm the right information, tools and knowledge before booking.',
  items: [
    service(
      '01',
      'Club membership',
      "We keep you updated on your car's safety and condition, and let you know if anything needs to be fixed or replaced.",
      'Save money on car ownership',
      'Paperwork and records kept organised',
      'Better support when you decide to sell',
    ),
    service(
      '02',
      'Repair shop',
      'We handle most maintenance tasks, like replacing fluids, filters, brakes, timing belts, and more.',
      'Fluids, filters, brakes and timing belts',
      'Manufacturer information checked first',
      'Transparent FRH pricing',
    ),
    service(
      '03',
      'Trusted referrals',
      'If we cannot do the job well in our shop, we refer you to someone else who is well-equipped and has good feedback from our customers.',
      'Checked before the appointment is confirmed',
      'Shop or referral decided in advance',
      'Well-equipped specialist when needed',
    ),
  ],
};

const enDecision: DecisionContent = {
  kicker: 'Before we confirm',
  title: 'We confirm the job before the appointment',
  body:
    'Before confirming an appointment, we make sure we have the correct information from the car manufacturer, the right tools, and the necessary knowledge.',
  row1Label: 'Procedure',
  row1Value: 'Manufacturer guidance available',
  row2Label: 'Tools',
  row2Value: 'Correct equipment confirmed',
  row3Label: 'Experience',
  row3Value: 'Suitable team member assigned',
  row4Label: 'Estimate',
  row4Value: 'Cost shared before repair',
};

const enPricing: PricingContent = {
  kicker: 'Membership & repair shop prices',
  title: 'Transparent prices',
  body:
    'Choose between a flexible monthly membership or a longer-term commitment, while repair work follows a clear FRH system so everything stays straightforward and transparent.',
  membershipTitle: 'Club membership',
  planFiveYearLabel: '5Y promo',
  planMonthlyLabel: 'Monthly',
  monthlyAmount: '€14.10',
  monthlyPeriod: '/ month',
  monthlyCopy:
    'Flexible monthly membership, with the option to transfer the remaining time to your next car if you sell.',
  monthlyBadge: '',
  monthlyBenefit1: 'Monthly plan: €14.10 per month',
  monthlyBenefit2:
    'No upfront five-year commitment while keeping the membership flexible',
  monthlyBenefit3:
    'Membership can be transferred to a new car if you sell the current one',
  monthlySummary1: 'Monthly billing',
  monthlySummary2: 'Move to 5Y later if you want extra savings',
  fiveYearAmount: '€500',
  fiveYearPeriod: '/ 5 years',
  fiveYearCopy:
    'One payment for five years, with the remaining membership still transferable to your next car if you sell.',
  fiveYearBadge: 'Best value',
  fiveYearBenefit1: 'Five-year plan: €500 total, equal to €8.33/month',
  fiveYearBenefit2: 'Five-year plan saves 40.89% versus monthly payment',
  fiveYearBenefit3:
    'Membership can be transferred to a new car if you sell the current one',
  fiveYearSummary1: '€8.33/mo equivalent',
  fiveYearSummary2: 'Save 40.89% vs monthly',
  membershipFootnote:
    "There's a good chance you can get this money back later, making the membership effectively free. Check the FAQs for more details.",
  repairTitle: 'Repair shop',
  repairAmount: '€32',
  repairPeriod: '/ FRH',
  repairCopy:
    'FRH is the manufacturer-set standard time for each job, so labour stays predictable and you do not pay for workshop delay.',
  repairBenefit1:
    'You pay the manufacturer-estimated job time, not extra time caused by delay',
  repairBenefit2: 'Parts are charged at supplier price with invoice transparency',
  repairBenefit3: 'Part handling fee: 0.147 FRH, currently €4.70 per part',
  repairBenefit4:
    'Non-members pay 20% additional FRH time for reports and logbook setup',
  repairSummary1: '€32 per FRH',
  repairSummary2: '€4.70 handling per part',
  repairFootnote:
    'Additional sourcing fees may apply only when a part requires special collection or off-island ordering. We inform you first.',
};

const enLocation: LocationContent = {
  kicker: 'Location & contacts',
  title: 'Find us in Caniço',
  body: 'Our Madeira shop is in Caniço, right by the highway.',
  cardTitle: 'The best way to contact us',
  addressLabel: 'Reference address',
  addressText: 'R. da Calçada 1, 9125-065 Caniço, next to the Grey Park building.',
  coordinatesLabel: 'Coordinates',
  coordinatesText: `32°38'54.1"N 16°50'31.6"W`,
  transitNote:
    'A bus stop is approximately 50 metres from the shop. After dropping off your car, you can reach central Funchal by bus 155, or use buses 109, 136 and 157.',
  emailLead:
    'For not urgent matters, please use email.',
};

const enFaq: FaqContent = {
  title: 'FAQ',
  searchPlaceholder: 'Search a question',
  emptyState:
    'No matching questions yet. Try a broader search term or switch back to all topics.',
  chips: [
    chip('All topics', 'all'),
    chip('Membership', 'membership'),
    chip('Pricing', 'pricing'),
    chip('Repairs', 'repairs'),
    chip('Records', 'records'),
    chip('Appointments', 'appointments'),
  ],
  items: [
    faq(
      'Who is the club for?',
      'It is best suited for busy professionals, drivers who find regular car care challenging, and families who rely on their vehicles daily and want to avoid unexpected repairs.',
      'membership',
    ),
    faq(
      'How do I know if the club is for me?',
      'If you want someone to handle routine maintenance and paperwork, help you avoid unnecessary spending, and help you get more money when selling your car, then the club is for you.',
      'membership',
    ),
    faq(
      'What do I get as a member?',
      'As a member, you stop worrying about your car’s condition, save money throughout ownership, and can get a higher price when you sell. Other benefits include repair savings and priority scheduling.',
      'membership',
    ),
    faq(
      'How much does membership cost?',
      'Membership costs €14.10 per month with monthly payment, or €500 for 5 years, equal to €8.33 per month and a 40.89 percent saving overall.',
      'pricing',
    ),
    faq(
      'How can membership be free?',
      'When you sell your car, the records, photos, and maintenance history may help make it more appealing to buyers. If you sell for €500 more, that covers the membership cost.',
      'membership',
    ),
    faq(
      'Can I cancel and get a refund?',
      'Yes. Monthly payments are not refunded. If you paid €500 upfront for 5 years, the refund is calculated by deducting the equivalent monthly cost for elapsed months, including the current month, and returning the remaining balance.',
      'membership',
    ),
    faq(
      'Do members get priority service?',
      'Yes. Members are prioritised first for appointments and question responses.',
      'appointments',
    ),
    faq(
      'What is the logbook?',
      'The logbook is an online record containing documents, repair history, car information, and service media. It can help avoid unnecessary repairs and support resale value.',
      'records',
    ),
    faq(
      'What services do you offer?',
      'There is no fixed list. Whether a job is accepted depends on proper manufacturer procedures, the right tools, and an experienced team member for that work.',
      'repairs',
    ),
    faq(
      'What are your prices for services?',
      'Service prices are based on the Flat Rate Hour set by the car manufacturer rather than the shop’s actual elapsed time. Non-members also pay 20 percent extra FRH time for reports and logbook preparation.',
      'pricing',
      'estimate payment invoice pricing frh flat rate hour service price',
    ),
    faq(
      'What are your prices for parts?',
      'Parts are charged at the same wholesale supplier price the shop pays, plus a 0.147 FRH handling fee per part. Extra sourcing fees can apply for island driving or outside-island online orders when necessary.',
      'pricing',
    ),
    faq(
      'Do you work on all car brands?',
      'Only when reliable manufacturer information, the right tools, and an experienced team member are available for the required system or repair.',
      'repairs',
    ),
    faq(
      'Do I need an appointment, or can you just walk in?',
      'You do not need a formal appointment, but you must contact the shop before coming so it can confirm that everything needed for your car is available and advise on the best day.',
      'appointments',
    ),
    faq(
      'Do you provide an estimate before repair?',
      'Yes. The cost is explained before work starts, and any extra work is communicated separately with supporting photo or video evidence when needed.',
      'appointments',
    ),
  ],
};

const enCta: CtaContent = {
  kicker: 'Questions or visit?',
  title: 'Send us a message about your car.',
  body: 'Text us with any questions about your car or to schedule your visit.',
  whatsappLabel: 'Message on WhatsApp',
  emailLabel: 'carkeepersclub@gmail.com',
};

const enFooter: FooterContent = {
  copy: '2024 © Car Keepers Club · Madeira',
  privacyLabel: 'Privacy',
  termsLabel: 'Terms',
  homeLabel: 'Home',
};

const ptSeo: SeoContent = {
  metaTitle: 'Madeira - Car Keepers Club',
  metaDescription:
    'Car Keepers Club & Oficina na Madeira. Um local onde acompanhamos continuamente o seu carro, evitando reparações dispendiosas e preservando o valor do automóvel.',
};

const ptNav: NavContent = {
  servicesLabel: 'Serviços',
  aboutLabel: 'Sobre nós',
  pricingLabel: 'Preços',
  locationLabel: 'Localização',
  faqLabel: 'Perguntas frequentes',
  whatsappLabel: 'WhatsApp',
  langSwitchLabel: 'EN',
};

const ptHero: HeroContent = {
  title: 'Car Care made simple',
  lead:
    'Um local onde acompanhamos continuamente o seu carro, evitando reparações dispendiosas e preservando o valor do automóvel.',
  primaryCta: 'Agendar visita',
  secondaryCta: 'Ver preços',
  cardTitle: 'Acompanhamento contínuo.',
  cardBody:
    'Vamos atualizando a segurança e o estado do seu carro, organizamos registos e documentação, e avisamos quando algo precisa de ser reparado ou substituído.',
  tag1: 'Atualizações de segurança',
  tag2: 'Registos & documentos',
  tag3: 'Histórico de serviço',
  tag4: 'Preservar valor de revenda',
  quickLinksLabel: 'Atalhos rápidos',
  quickLink1: 'Serviços do clube & oficina',
  quickLink2: 'Adesão & preços da oficina',
  quickLink3: 'Localização & contactos',
  quickLink4: 'Perguntas & respostas',
};

const ptServices: ServicesContent = {
  kicker: 'Serviços do clube & oficina',
  title: 'Como cuidamos do seu carro',
  body:
    'No clube, mantemo-lo atualizado sobre a segurança e o estado do carro. Na oficina, tratamos da maioria das manutenções e confirmamos a informação, as ferramentas e o conhecimento antes da marcação.',
  items: [
    service(
      '01',
      'Adesão ao clube',
      'Mantemo-lo atualizado sobre a segurança e o estado do seu carro, e avisamo-lo se algo precisar de ser reparado ou substituído.',
      'Poupar dinheiro na posse do carro',
      'Papelada e registos organizados',
      'Melhor apoio quando decidir vender',
    ),
    service(
      '02',
      'Oficina',
      'Tratamos da maioria das tarefas de manutenção, como troca de fluidos, filtros, travões, correias de distribuição e mais.',
      'Fluidos, filtros, travões e correias de distribuição',
      'Informação do fabricante verificada primeiro',
      'Preços transparentes por HFR',
    ),
    service(
      '03',
      'Parceiros recomendados',
      'Se não conseguirmos fazer bem o trabalho na nossa oficina, encaminhamo-lo para alguém bem equipado e com bom feedback dos nossos clientes.',
      'Verificado antes de confirmar a visita',
      'Oficina ou encaminhamento decidido antecipadamente',
      'Especialista bem equipado quando necessário',
    ),
  ],
};

const ptDecision: DecisionContent = {
  kicker: 'Antes de confirmar',
  title: 'Confirmamos o trabalho antes da visita',
  body:
    'Antes de confirmar uma marcação, garantimos que temos a informação correta do fabricante, as ferramentas certas e o conhecimento necessário.',
  row1Label: 'Procedimento',
  row1Value: 'Documentação do fabricante disponível',
  row2Label: 'Ferramentas',
  row2Value: 'Equipamento correto confirmado',
  row3Label: 'Experiência',
  row3Value: 'Técnico adequado atribuído',
  row4Label: 'Orçamento',
  row4Value: 'Custo comunicado antes da reparação',
};

const ptPricing: PricingContent = {
  kicker: 'Adesão & preços da oficina',
  title: 'Preços transparentes',
  body:
    'A adesão custa 14,10 euros por mês com pagamento mensal, ou 500 euros por 5 anos. O trabalho da oficina é cobrado a 32 euros por Hora de Tabela do Fabricante (HFR).',
  membershipTitle: 'Adesão ao clube',
  planFiveYearLabel: 'Promo 5 anos',
  planMonthlyLabel: 'Mensal',
  monthlyAmount: '€14,10',
  monthlyPeriod: '/ mês',
  monthlyCopy:
    'Adesão mensal flexível, com a possibilidade de transferir o tempo restante para o seu próximo carro se o vender.',
  monthlyBadge: '',
  monthlyBenefit1: 'Plano mensal: €14,10 por mês',
  monthlyBenefit2: 'Sem compromisso de pagar cinco anos de uma só vez',
  monthlyBenefit3: 'A adesão pode ser transferida para um novo carro se vender o atual',
  monthlySummary1: 'Pagamento mensal',
  monthlySummary2: 'Mude para 5 anos depois se quiser poupar mais',
  fiveYearAmount: '€500',
  fiveYearPeriod: '/ 5 anos',
  fiveYearCopy:
    'Um pagamento único para cinco anos, com a adesão restante ainda transferível para o seu próximo carro se o vender.',
  fiveYearBadge: 'Melhor valor',
  fiveYearBenefit1: 'Plano de cinco anos: €500 no total, o equivalente a €8,33/mês',
  fiveYearBenefit2:
    'O plano de cinco anos permite poupar 40,89% face ao pagamento mensal',
  fiveYearBenefit3: 'A adesão pode ser transferida para um novo carro se vender o atual',
  fiveYearSummary1: 'Equivale a €8,33/mês',
  fiveYearSummary2: 'Poupe 40,89% face ao mensal',
  membershipFootnote:
    'Há uma boa probabilidade de recuperar este valor mais tarde, tornando a adesão efetivamente gratuita. Consulte as perguntas frequentes para mais detalhes.',
  repairTitle: 'Oficina',
  repairAmount: '€32',
  repairPeriod: '/ HFR',
  repairCopy:
    'HFR é o tempo padrão definido pelo fabricante para cada serviço, mantendo a mão de obra previsível e transparente.',
  repairBenefit1:
    'Paga o tempo estimado pelo fabricante, não tempo extra causado por atrasos',
  repairBenefit2: 'Peças cobradas ao preço do fornecedor, com transparência de fatura',
  repairBenefit3:
    'Taxa de manuseamento de peças: 0,147 HFR, atualmente €4,70 por peça',
  repairBenefit4:
    'Clientes sem adesão pagam mais 20% de HFR para relatórios e configuração do livro de manutenção',
  repairSummary1: '€32 por HFR',
  repairSummary2: '€4,70 de manuseamento por peça',
  repairFootnote:
    'Só se aplicam custos adicionais de obtenção de peças quando uma peça exige recolha especial ou encomenda fora da ilha. Informamo-lo sempre antes.',
};

const ptLocation: LocationContent = {
  kicker: 'Localização & contactos',
  title: 'Encontre-nos no Caniço',
  body: 'A nossa oficina da Madeira fica no Caniço, mesmo junto à via rápida.',
  cardTitle: 'A melhor forma de contactar',
  addressLabel: 'Morada de referência',
  addressText: 'R. da Calçada 1, 9125-065 Caniço, junto ao edifício Grey Park.',
  coordinatesLabel: 'Coordenadas',
  coordinatesText: `32°38'54.1"N 16°50'31.6"W`,
  transitNote:
    'Há uma paragem de autocarro a cerca de 50 metros da oficina. Depois de deixar o carro, pode chegar ao centro do Funchal no autocarro 155, ou usar as linhas 109, 136 e 157.',
  emailLead:
    'Para assuntos não urgentes, utilize o email. Para algo urgente, envie mensagem por WhatsApp primeiro.',
};

const ptFaq: FaqContent = {
  title: 'FAQ',
  searchPlaceholder: 'Pesquisar uma pergunta',
  emptyState:
    'Ainda não encontrámos perguntas correspondentes. Experimente um termo mais geral ou volte a todos os temas.',
  chips: [
    chip('Todos os temas', 'all'),
    chip('Adesão', 'membership'),
    chip('Preços', 'pricing'),
    chip('Reparações', 'repairs'),
    chip('Registos', 'records'),
    chip('Marcações', 'appointments'),
  ],
  items: [
    faq(
      'Para quem é o clube?',
      'É mais indicado para profissionais ocupados, condutores que têm dificuldade com a manutenção regular do carro, e famílias que dependem do veículo no dia a dia e querem evitar reparações inesperadas.',
      'membership',
    ),
    faq(
      'Como sei se o clube é para mim?',
      'Se quer alguém para tratar da manutenção de rotina e da papelada, ajudá-lo a evitar gastos desnecessários e ajudá-lo a vender o carro por mais no futuro, então o clube é para si.',
      'membership',
    ),
    faq(
      'O que recebo como membro?',
      'Como membro, deixa de se preocupar com o estado do carro, poupa dinheiro durante a posse e pode conseguir um preço mais alto quando vende. Outros benefícios incluem poupança em reparações e prioridade na marcação.',
      'membership',
    ),
    faq(
      'Quanto custa a adesão?',
      'A adesão custa €14,10 por mês com pagamento mensal, ou €500 por 5 anos, o equivalente a €8,33 por mês e uma poupança total de 40,89 por cento.',
      'pricing',
    ),
    faq(
      'Como pode a adesão ser gratuita?',
      'Quando vender o carro, os registos, fotografias e histórico de manutenção podem torná-lo mais apelativo para compradores. Se vender por mais €500, isso cobre o custo da adesão.',
      'membership',
    ),
    faq(
      'Posso cancelar e receber reembolso?',
      'Sim. Os pagamentos mensais não são reembolsados. Se pagou €500 antecipadamente por 5 anos, o reembolso é calculado deduzindo o equivalente mensal aos meses já decorridos, incluindo o mês atual, e devolvendo o saldo restante.',
      'membership',
    ),
    faq(
      'Os membros têm atendimento prioritário?',
      'Sim. Os membros têm prioridade nas marcações e nas respostas às suas questões.',
      'appointments',
    ),
    faq(
      'O que é o livro de manutenção?',
      'O livro de manutenção é um registo online com documentos, histórico de reparações, informação do carro e material do serviço. Pode ajudar a evitar reparações desnecessárias e apoiar o valor de revenda.',
      'records',
    ),
    faq(
      'Que serviços oferecem?',
      'Não existe uma lista fixa. A aceitação de um trabalho depende de existirem procedimentos corretos do fabricante, as ferramentas certas e um membro da equipa com experiência nesse trabalho.',
      'repairs',
    ),
    faq(
      'Quais são os vossos preços para serviços?',
      'Os preços dos serviços baseiam-se na Hora de Tabela do Fabricante, em vez do tempo real decorrido na oficina. Os não-membros também pagam mais 20 por cento de HFR para relatórios e preparação do livro de manutenção.',
      'pricing',
      'orçamento pagamento fatura preços hfr hora fabricante preço serviço',
    ),
    faq(
      'Quais são os vossos preços para peças?',
      'As peças são cobradas ao mesmo preço grossista pago pela oficina, mais uma taxa de manuseamento de 0,147 HFR por peça. Podem existir custos adicionais de obtenção quando necessário procurar na ilha ou encomendar de fora.',
      'pricing',
    ),
    faq(
      'Trabalham com todas as marcas de automóveis?',
      'Só quando existe informação fiável do fabricante, ferramentas adequadas e um membro da equipa com experiência no sistema ou reparação necessária.',
      'repairs',
    ),
    faq(
      'Preciso de marcação ou posso simplesmente aparecer?',
      'Não precisa de uma marcação formal, mas deve contactar a oficina antes de vir para confirmar se tudo o que o carro precisa está disponível e qual o melhor dia.',
      'appointments',
    ),
    faq(
      'Fornecem um orçamento antes da reparação?',
      'Sim. O custo é explicado antes do trabalho começar, e qualquer trabalho extra é comunicado separadamente com apoio de fotografia ou vídeo quando necessário.',
      'appointments',
    ),
  ],
};

const ptCta: CtaContent = {
  kicker: 'Dúvidas ou visita?',
  title: 'Envie-nos uma mensagem sobre o seu carro.',
  body: 'Envie-nos mensagem com qualquer dúvida sobre o seu carro ou para marcar a sua visita.',
  whatsappLabel: 'Mensagem no WhatsApp',
  emailLabel: 'carkeepersclub@gmail.com',
};

const ptFooter: FooterContent = {
  copy: '2024 © Car Keepers Club · Madeira',
  privacyLabel: 'Privacidade',
  termsLabel: 'Termos',
  homeLabel: 'Página inicial',
};

export const defaultContent: CarKeepersClubContent = {
  brand: {
    homeHref: 'https://carkeepers.club/',
    privacyHref: 'https://carkeepers.club/privacy-policy',
    termsHref: 'https://carkeepers.club/terms-and-conditions',
    whatsapp: '+351927927103',
    email: 'carkeepersclub@gmail.com',
    addressHref: 'https://maps.app.goo.gl/VJq6nzbKtkkfc6ee7',
    coordinatesHref: 'https://www.google.com/maps?q=32.648361,-16.842111',
  },
  enSeo,
  enNav,
  enHero,
  enServices,
  enDecision,
  enPricing,
  enLocation,
  enFaq,
  enCta,
  enFooter,
  ptSeo,
  ptNav,
  ptHero,
  ptServices,
  ptDecision,
  ptPricing,
  ptLocation,
  ptFaq,
  ptCta,
  ptFooter,
};

export default defaultContent;
